import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { AuthUserContext } from './context';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'; // Modular imports from Firebase v9

const withAuthorization = (condition) => (Component) => {
  const WithAuthorization = (props) => {
    const navigate = useNavigate(); // Use useNavigate for navigation
    const sessionContext = useContext(AuthUserContext);
    const setContextAuthUser = sessionContext?.setAuthUser;
    const [authUser, setAuthUser] = useState(sessionContext?.authUser || null);
    const auth = getAuth(); // Initialize Firebase Auth instance

    useEffect(() => {
      const listener = onAuthStateChanged(
        auth, // Pass the auth instance
        async (user) => {
          if (user) {
            const token = await user.getIdToken(true);
            const isGoogleProvider = user.providerData.some(
              (provider) => provider.providerId === 'google.com'
            );
            
            setAuthUser(user);
            if (typeof setContextAuthUser === 'function') {
              setContextAuthUser(user);
            }

            // Check token expiration
            const decodedToken = await user.getIdTokenResult();
            const expirationTime = decodedToken.expirationTime;
            const now = new Date().getTime();
            if (expirationTime && new Date(expirationTime).getTime() < now) {
              // Token is expired, log out
              await signOut(auth);
              navigate('/login'); // Use useNavigate for redirection
            }
          } else {
            setAuthUser(null);
            if (typeof setContextAuthUser === 'function') {
              setContextAuthUser(null);
            }
            navigate('/login'); // Redirect to login if user is not authenticated
          }
        },
        (error) => {
          console.error('onAuthStateChanged error:', error);
          navigate('/login'); // Redirect on error
        }
      );

      return () => listener(); // Cleanup listener on unmount
    }, [auth, navigate, setContextAuthUser]); // Keep local and shared auth context in sync

    const effectiveAuthUser = sessionContext?.authUser ?? authUser;
    return condition(effectiveAuthUser) ? <Component {...props} /> : null;
  };

  return WithAuthorization;
};

export default withAuthorization;
